import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Target, MapPin, Calendar, Users, TrendingUp, IndianRupee, ChevronRight,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const STATUS_BADGE = {
  planned:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  live:      'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  completed: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmt = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


export default function HealthCampsList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [camps, setCamps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiFetch('engage/health-camps/');
        if (!cancelled) setCamps(resp?.results || resp?.data?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => camps.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const hay = [c.name, c.location_name, c.specialty_focus].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [camps, search, statusFilter]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-sm">
            <Target className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Health Camps</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} camps`}
            </p>
          </div>
        </div>
        <button onClick={() => navigate('/engage/health-camps/new')}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-pink-600 text-white text-sm shadow-sm hover:bg-pink-700">
          <Plus className="w-3.5 h-3.5" /> Plan Camp
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search camps by name, location, specialty…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-pink-500 text-gray-900 dark:text-white" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
          <option value="all">All statuses</option>
          <option value="planned">Planned</option>
          <option value="live">Live</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        : filtered.length === 0 ? <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">No health camps yet.</div>
        : filtered.map(c => {
          const attended = c.actual_footfall || 0;
          const spend = Number(c.actual_spend || c.budget || 0);
          const cpa = attended > 0 ? spend / attended : 0;
          return (
            <button key={c.id} onClick={() => navigate(`/engage/health-camps/${c.id}`)}
              className="group text-left bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-pink-300 dark:hover:border-pink-700 transition-all">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{c.name}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[c.status] || STATUS_BADGE.planned)}>
                      {c.status || 'planned'}
                    </span>
                    {c.specialty_focus && (
                      <span className="inline-block px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                        {c.specialty_focus}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="space-y-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                {c.location_name && <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3" /> {c.location_name}</div>}
                {c.start_datetime && <div className="flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {fmt(c.start_datetime)}</div>}
              </div>
              <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 grid grid-cols-3 gap-2 text-center">
                <div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{attended}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 flex items-center justify-center gap-0.5"><Users className="w-2.5 h-2.5" /> Attended</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">{c.enquiries_generated || 0}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 flex items-center justify-center gap-0.5"><TrendingUp className="w-2.5 h-2.5" /> Leads</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-amber-600 dark:text-amber-400">₹ {formatINR(cpa)}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500 flex items-center justify-center gap-0.5"><IndianRupee className="w-2.5 h-2.5" /> CPA</div>
                </div>
              </div>
              <div className="flex justify-end mt-2">
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-pink-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
